import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  Box,
  InputBase,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Chip,
  useTheme,
} from "@mui/material";
import { Search, Close, NorthEast, SearchOff } from "@mui/icons-material";
import { buildSearchIndex, searchSite } from "utils/searchIndex";

const SiteSearchDialog = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  // Rebuild the index whenever the language changes so result labels follow the
  // active locale. The searchable haystack always spans all languages, so a
  // query in any language matches regardless of the language being viewed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const index = useMemo(() => buildSearchIndex(t, i18n), [t, i18n.language]);
  const results = useMemo(() => searchSite(index, query), [index, query]);

  // Reset state each time the dialog opens, and focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const go = (entry) => {
    if (!entry) return;
    onClose();
    navigate(entry.hash ? `${entry.path}#${entry.hash}` : entry.path);
  };

  const handleKeyDown = (e) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIndex]);
    }
  };

  // Keep the active result scrolled into view.
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          position: "fixed",
          top: { xs: 24, sm: 72 },
          m: 0,
          width: "100%",
          maxHeight: { xs: "85vh", sm: "70vh" },
          overflow: "hidden",
        },
      }}
    >
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Search sx={{ color: "text.secondary" }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("search.placeholder")}
          fullWidth
          sx={{ fontSize: "1.05rem" }}
        />
        <IconButton size="small" onClick={onClose} aria-label={t("common.close")}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box ref={listRef} sx={{ overflowY: "auto" }}>
        {query.trim() === "" ? (
          <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
            <Search sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("search.hint")}
            </Typography>
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
            <SearchOff sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("search.noResults", { query })}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {results.map((entry, idx) => {
              const active = idx === activeIndex;
              return (
                <React.Fragment key={entry.id}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItemButton
                    data-idx={idx}
                    selected={active}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => go(entry)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      "&.Mui-selected": {
                        backgroundColor: `${theme.palette.secondary.main}14`,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: `${theme.palette.secondary.main}22`,
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="text.primary"
                            sx={{ flexShrink: 1 }}
                          >
                            {entry.title}
                          </Typography>
                          <Chip
                            label={entry.group}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {entry.description}
                        </Typography>
                      }
                    />
                    {active && (
                      <NorthEast sx={{ fontSize: 16, color: "secondary.main", ml: 1 }} />
                    )}
                  </ListItemButton>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default SiteSearchDialog;
